import { Button, Checkbox } from '@sr/ui-library';
import type { CsvColumn } from '../utils/csv/toCsvChunk';

const GROUPS = [
  ['default', 'On screen'],
  ['details', 'More details'],
  ['ids', 'Identifiers'],
] as const;

/** Ticked when nothing is saved yet, and by Reset: the on-screen columns. */
export const defaultCsvColumnIds = <T, C>(columns: CsvColumn<T, C>[]) =>
  new Set(
    columns
      .filter((column) => column.group === 'default')
      .map((column) => column.id),
  );

type CsvColumnPickerProps<T, C> = {
  /** The columns on offer, in spec order. */
  columns: CsvColumn<T, C>[];
  onSelectionChange: (selectedIds: Set<string>) => void;
  selectedIds: Set<string>;
};

/** The checkboxes in the CSV download dialog, grouped as in the column specs. */
export const CsvColumnPicker = <T, C>({
  columns,
  onSelectionChange,
  selectedIds,
}: CsvColumnPickerProps<T, C>) => {
  const selectedCount = columns.filter((column) =>
    selectedIds.has(column.id),
  ).length;

  const handleToggle = (id: string, isChecked: boolean) => {
    const nextIds = new Set(selectedIds);

    if (isChecked) nextIds.add(id);
    else nextIds.delete(id);

    onSelectionChange(nextIds);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">
          Columns ({selectedCount} of {columns.length})
        </p>

        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              onSelectionChange(new Set(columns.map((column) => column.id)))
            }
          >
            Select all
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => onSelectionChange(defaultCsvColumnIds(columns))}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Cuts a row in half, so the box doesn't look like the end of the
          list. At 18rem the cut fell in the gap between two rows and hid
          that more columns sit below. */}
      <div className="max-h-[17rem] space-y-3 overflow-y-auto">
        {GROUPS.map(([group, groupLabel]) => {
          const groupColumns = columns.filter(
            (column) => column.group === group,
          );

          return (
            groupColumns.length > 0 && (
              <div
                key={group}
                aria-label={groupLabel}
                className="space-y-2"
                role="group"
              >
                <p className="text-muted-foreground">{groupLabel}</p>

                <div className="grid grid-cols-2 gap-2">
                  {groupColumns.map((column) => (
                    <Checkbox
                      id={`csv-column-${column.id}`}
                      key={column.id}
                      checkboxContent={column.header}
                      checked={selectedIds.has(column.id)}
                      onCheckedChange={(checked) =>
                        handleToggle(column.id, checked === true)
                      }
                    />
                  ))}
                </div>
              </div>
            )
          );
        })}
      </div>
    </div>
  );
};
